import { useLayoutEffect, useMemo, useState } from 'react'
import { useInstantSearch } from 'react-instantsearch'
import type { UiState } from 'instantsearch.js'
import { mapValues, pick } from 'lodash-es'

import { isTrackedSearchError } from 'Algolia/searchClient/createTrackedSearchClient'

type FailureDecision =
  | { hasDroppedRefinements: boolean; mode: 'default-list' }
  | { error: Error; mode: 'error' }

const UI_STATE_KEYS_KEPT_ON_FALLBACK = [
  'configure',
  'hitsPerPage',
  'query',
  'sortBy',
] as const

const removeRefinements = (uiState: UiState): UiState =>
  mapValues(uiState, indexUiState =>
    pick(indexUiState, UI_STATE_KEYS_KEPT_ON_FALLBACK),
  )

export const useInstantsearchListFallback = () => {
  const [failureDecision, setFailureDecision] = useState<FailureDecision>()
  const instantSearch = useInstantSearch({ catchError: true })
  const { error: instantSearchError, results, setUiState } = instantSearch
  const trackedError = isTrackedSearchError(instantSearchError)
    ? instantSearchError
    : undefined
  const isLatestTrackedError = trackedError?.isLatestRequest() === true

  const incomingFailureDecision = useMemo<FailureDecision | undefined>(() => {
    if (instantSearchError && !trackedError) {
      return { error: instantSearchError, mode: 'error' }
    }

    if (!isLatestTrackedError || !trackedError) {
      return undefined
    }

    return {
      hasDroppedRefinements: trackedError.hasActiveRefinements,
      mode: 'default-list',
    }
  }, [instantSearchError, isLatestTrackedError, trackedError])

  useLayoutEffect(() => {
    if (!incomingFailureDecision || failureDecision) {
      return
    }

    // Persist the first failure before removing search widgets can issue a
    // cleanup request with a different refinement state.
    setFailureDecision(incomingFailureDecision)

    // The error rollback skips onStateChange, so clear the refinements
    // explicitly or the persisted UI state replays them on the next visit.
    if (
      incomingFailureDecision.mode === 'default-list' &&
      incomingFailureDecision.hasDroppedRefinements
    ) {
      setUiState(removeRefinements)
    }
  }, [failureDecision, incomingFailureDecision, setUiState])

  const effectiveFailureDecision = failureDecision ?? incomingFailureDecision

  const shouldUseDefaultListTable =
    effectiveFailureDecision?.mode === 'default-list'

  const hasDroppedRefinements =
    effectiveFailureDecision?.mode === 'default-list' &&
    effectiveFailureDecision.hasDroppedRefinements

  // Artificial results are placeholders until the first response arrives.
  // eslint-disable-next-line no-underscore-dangle
  const isLoading = results?.__isArtificial === true

  let error = instantSearchError

  if (effectiveFailureDecision?.mode === 'error') {
    const { error: failureError } = effectiveFailureDecision

    error = failureError
  } else if (trackedError && !isLatestTrackedError) {
    error = undefined
  }

  return {
    ...instantSearch,
    error,
    hasDroppedRefinements,
    isLoading,
    shouldUseDefaultListTable,
  }
}
